import { LitElement, PropertyValues, TemplateResult, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import { UrlValidator as validateUrl } from "@slhuicore/utils";
import { ApplicationItemInterface, UserInterface } from "@slhuinav/base";
import { CartInterface } from "@slhuinav/base/interfaces/cart";
import { SlhNavigationSidebar } from "@slhuinav/sidebar";
import { SlhNavigationSidebarState } from "@slhuinav/sidebar/src/sidebar.type";
import { SlhNavigationHeaderbar } from "@slhuinav/headerbar";
import { NavigationDataInterface } from "@slhuinav/base/interfaces/navigation-data";
import { style } from "./assets/nav.generated-style";

/**
 * Navigation component
 */
@customElement("slh-nav")
export default class SlhNavigation extends LitElement {
  static styles = style;

  /**
   * Navigation data retrieval URL
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  source: string = "";

  /**
   * Headerbar user data
   */
  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  user?: UserInterface;

  /**
   * Sidebar home navigation item
   */
  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  home?: ApplicationItemInterface;

  /**
   * Headerbar sign in url
   */
  @property({
    type: Object,
    attribute: "sign-in",
    reflect: true,
  })
  signIn?: ApplicationItemInterface;

  /**
   * Headerbar sign out url
   */
  @property({
    type: Object,
    attribute: "sign-out",
    reflect: true,
  })
  signOut?: ApplicationItemInterface;

  /**
   * Sidebar application items
   */
  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  applications: Array<ApplicationItemInterface> = [];

  /**
   * Headerbar user navigation
   */
  @property({
    type: Array,
    attribute: "user-applications",
    reflect: true,
  })
  userApplications: Array<ApplicationItemInterface> = [];

  /**
   * Organizations
   */
  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  organizations: Array<ApplicationItemInterface> = [];

  /**
   * Headerbar settings navigation
   */
  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  settings: Array<ApplicationItemInterface> = [];

  /**
   * Language switch URLs
   */
  @property({
    type: Array,
    attribute: "language-switch",
    reflect: true,
  })
  languageSwitch: Array<ApplicationItemInterface> = [];

  /**
   * Render active application title in headerbar
   */
  @property({
    type: Boolean,
    attribute: "show-title",
    reflect: true,
  })
  showTitle: boolean = false;

  @property({
    type: Object,
    attribute: "cart",
    reflect: true,
  })
  cart?: CartInterface;

  private headerbarRef: Ref<SlhNavigationHeaderbar> = createRef();

  private sidebarRef: Ref<SlhNavigationSidebar> = createRef();

  @state()
  private connected = false;

  @state()
  private loaded = false;

  connectedCallback() {
    super.connectedCallback();
    this.connected = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Renders headerbar user application item with provided identifier in active state (current app)
   */
  public setActiveUserApplication(identifier: string): void {
    this.headerbarRef.value!.setActiveUserApplication(identifier);
  }

  /**
   * Renders headerbar language switch application item with provided identifier in active state (current app)
   */
  public setActiveLanguageSwitchApplication(identifier: string): void {
    this.headerbarRef.value!.setActiveLanguageSwitchApplication(identifier);
  }

  /**
   * Renders headerbar settings item with provided identifier in active state
   */
  public setActiveSettingsItem(identifier: string | null): void {
    this.headerbarRef.value!.setActiveSettingsItem(identifier);
  }

  firstUpdated() {
    if (this.source) {
      this.loadNavigationData();
    } else {
      this.setNavigationData(this as NavigationDataInterface);
    }
    this.setHeaderbarAppTitle();
  }

  /**
   * Pushes changed nav properties down to headerbar and sidebar child components
   * after each render. Using updated() ensures refs are
   * populated and the DOM is in sync before any imperative assignment runs.
   */
  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (this.sidebarRef?.value) {
      if (changedProperties.has("applications")) {
        this.sidebarRef.value.applications = this.applications;
      }
      if (changedProperties.has("home")) {
        this.sidebarRef.value.home = this.home;
      }
    }

    if (this.headerbarRef?.value) {
      if (changedProperties.has("user")) {
        this.headerbarRef.value.user = this.user;
      }
      if (
        changedProperties.has("applications") ||
        changedProperties.has("showTitle")
      ) {
        this.setHeaderbarAppTitle();
      }
      if (changedProperties.has("userApplications")) {
        this.headerbarRef.value.userApplications = this.userApplications;
      }
      if (changedProperties.has("settings")) {
        this.headerbarRef.value.settings = this.settings;
      }
      if (changedProperties.has("signIn")) {
        this.headerbarRef.value.signIn = this.signIn;
      }
      if (changedProperties.has("signOut")) {
        this.headerbarRef.value.signOut = this.signOut;
      }
      if (changedProperties.has("organizations")) {
        this.headerbarRef.value.organizations = this.organizations;
      }
      if (changedProperties.has("languageSwitch")) {
        this.headerbarRef.value.languageSwitch = this.languageSwitch;
      }
      if (changedProperties.has("cart")) {
        this.headerbarRef.value.cart = this.cart;
      }
    }
  }

  render(): TemplateResult {
    return html`<div class="nav-wrapper">
      <slh-nav-headerbar ${ref(this.headerbarRef)}></slh-nav-headerbar
      ><slh-nav-sidebar
        state="${SlhNavigationSidebarState.HIDDEN}"
        ${ref(this.sidebarRef)}
      ></slh-nav-sidebar>
    </div>`;
  }

  private setHeaderbarAppTitle() {
    if (this.showTitle) {
      this.applications.forEach((appItem: ApplicationItemInterface) => {
        if (appItem.active) {
          this.headerbarRef.value!.appTitle = appItem.title ?? "";
        }
      });
    } else {
      this.headerbarRef.value!.appTitle = "";
    }
  }

  private loadNavigationData(): void {
    this.assertValidUrl();
    fetch(this.source, {
      method: "GET",
      // TODO cors, credentials
      redirect: "follow",
      // credentials: "include", // force browser to send request with credentials included on both same-origin and cross-origin calls
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Invalid response: error: ${response.statusText}, code ${response.status}`,
          );
        }
        return response.json();
      })
      .then((data) => {
        this.loaded = true;
        this.render();
        this.setNavigationData(data);
      })
      .catch((error) => {
        console.error(
          "[slh-nav] An error has occured while fetching navigation data: ",
          error,
        );
      });
  }

  private setNavigationData(data: NavigationDataInterface): void {
    if (data.signIn !== undefined) {
      this.signIn = data.signIn;
    }
    if (data.user !== undefined) {
      this.user = data.user;
    }
    if (data.home !== undefined) {
      this.home = data.home;
    }
    if (data.applications !== undefined) {
      this.applications = data.applications;
    }
    if (data.userApplications !== undefined) {
      this.userApplications = data.userApplications;
    }
    if (data.signOut !== undefined) {
      this.signOut = data.signOut;
    }
    if (data.settings !== undefined) {
      this.settings = data.settings;
    }
    if (data.organizations !== undefined) {
      this.organizations = data.organizations;
    }
    if (data.languageSwitch !== undefined) {
      this.languageSwitch = data.languageSwitch;
    }
    if (data.cart !== undefined) {
      this.cart = data.cart;
    }
  }

  private assertValidUrl(): void {
    if (this.source === "" || !validateUrl(this.source)) {
      console.error(
        "[slh-nav] Valid URL must be provided to fetch navigation data",
      );
    }
  }
}
